import { useCallback, useMemo } from 'react'
import { logger, log } from '@/lib/logger'
import type { LogContext } from '@/lib/logger'

export interface UseLoggerOptions {
  component?: string
  function?: string
  userId?: string
  sessionId?: string
}

export function useLogger(options: UseLoggerOptions = {}) {
  const context = useMemo<LogContext>(() => ({
    component: options.component,
    function: options.function,
    userId: options.userId,
    sessionId: options.sessionId
  }), [options.component, options.function, options.userId, options.sessionId])

  const error = useCallback((message: string, data?: unknown, additionalContext?: LogContext) => {
    log.error(message, data, { ...context, ...additionalContext })
  }, [context])

  const warn = useCallback((message: string, data?: unknown, additionalContext?: LogContext) => {
    log.warn(message, data, { ...context, ...additionalContext })
  }, [context])

  const info = useCallback((message: string, data?: unknown, additionalContext?: LogContext) => {
    log.info(message, data, { ...context, ...additionalContext })
  }, [context])

  const debug = useCallback((message: string, data?: unknown, additionalContext?: LogContext) => {
    log.debug(message, data, { ...context, ...additionalContext })
  }, [context])

  const trace = useCallback((message: string, data?: unknown, additionalContext?: LogContext) => {
    log.trace(message, data, { ...context, ...additionalContext })
  }, [context])

  const api = useMemo(() => ({
    error,
    warn,
    info,
    debug,
    trace,
    logger,
    context
  }), [error, warn, info, debug, trace, context])

  return api
}

// Convenience hook for component-specific logging
export function useComponentLogger(componentName: string, options: Omit<UseLoggerOptions, 'component'> = {}) {
  return useLogger({ ...options, component: componentName })
}